import { FunctionalComponent } from 'preact';
import { memo } from 'preact/compat';
import flow from 'lodash/flow';
import cn from 'classnames';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
// import CallMadeIcon from '@material-ui/icons/CallMade';

import { getTranslation } from '../../../../../shared/chromeApi';
import { urlPrettifiers } from '../../slice/utils';
import styles from './styles.module.scss';
import { Suggestion, Engines } from '../../slice/types';
import { ENGINES } from '../../utils';
import { getRegEx } from './utils';

import whaleImage from './whale.svg';

const prettyfyUrl = flow(
  urlPrettifiers.removePrefix,
  urlPrettifiers.removeSuffix,
  urlPrettifiers.removeQuery
);

interface Props {
  suggestion: Suggestion;
  value: string;
  isActive: boolean;
  onClick: (s: Suggestion, evt: MouseEvent) => unknown;
  onMouseEnter: (id: string) => unknown;
  onRemove: (s: Suggestion) => unknown;
  engine: Engines;
  // customRef: RefCallback<HTMLElement>,
  customRef: (id: string, el: HTMLElement) => unknown;
  displayWhale: boolean;
}

const SuggestionElement: FunctionalComponent<Props> = ({
  suggestion,
  value,
  isActive,
  onClick,
  onRemove,
  engine,
  onMouseEnter,
  customRef,
  displayWhale,
}: Props) => {
  const type =
    suggestion.type === 'organic' && suggestion.provider
      ? 'ad'
      : suggestion.type;
  // const [faviconUrl, setFaviconUrl] = useState(suggestion.favIconUrl);

  // const resetFavicon = () => {
  //   setFaviconUrl(null);
  // }

  const handleRemove = () => {
    onRemove(suggestion);
  };

  const regex = getRegEx(value);
  const isLarge =
    suggestion.favIconUrl &&
    (suggestion.label || ['instantResult', 'instantResultAd'].includes(type));

  const renderIcon = () => {
    if (suggestion.favIconUrl) {
      return isLarge ? (
        <div
          className={styles.favicon}
          style={{ backgroundImage: `url(${suggestion.favIconUrl})` }}
        />
      ) : (
        <img src={suggestion.favIconUrl} alt="" style="width: 18px;" />
      );
    }

    if (['history', 'historyOrganic'].includes(type)) return <AccessTimeIcon />;

    // if (type === 'organic') return <CallMadeIcon />;

    return <SearchIcon />;
  };

  const renderTerm = () => {
    const getHTML = (type: 'term' | 'url', term) => {
      try {
        if (!regex || (type === 'term' && value.length < 2)) return term;

        return term.replace(
          regex,
          ['organic', 'organicProvided'].includes(type)
            ? '$1<b>$2</b>'
            : '<b>$1</b>$2'
        );
      } catch (err) {
        console.error('getHTML', err);
        return term;
      }
    };

    if (type === 'ad') {
      return (
        <span className={styles.itemTerm}>
          <img
            src={suggestion.impressionUrl}
            width="1"
            height="1"
            alt=""
            className={styles.itemImpression}
          />

          <span
            dangerouslySetInnerHTML={{
              __html: getHTML('term', suggestion.term),
            }}
          />

          {suggestion.displayUrl && (
            <span className={styles.urlLabel}>
              <span> - </span>
              <span
                dangerouslySetInnerHTML={{
                  __html: getHTML('url', prettyfyUrl(suggestion.displayUrl)),
                }}
              />
            </span>
          )}

          <span className={styles.adLabel}>
            {getTranslation('search_suggestion_ad')}
            {displayWhale && <img src={whaleImage} alt="whale" />}
          </span>
        </span>
      );
    }

    if (type === 'openTab') {
      const term = suggestion.term.substr(0, 34);
      return (
        <span className={styles.itemTerm}>
          <span dangerouslySetInnerHTML={{ __html: getHTML('term', term) }} />
          {suggestion.term.length > 34 ? '...' : ''}

          <span className={styles.openTabLabel}> - open tab</span>
        </span>
      );
    }

    if (type === 'urlCandidate') {
      return (
        <span className={styles.itemTerm}>
          <span
            className={styles.urlLabel}
            dangerouslySetInnerHTML={{
              __html: getHTML('term', suggestion.term),
            }}
          />
        </span>
      );
    }

    if (['instantResult', 'instantResultAd'].includes(type)) {
      return (
        <span className={styles.itemTerm}>
          <div
            className={styles.label}
            style={{ overflow: 'visible' }}
            dangerouslySetInnerHTML={{
              __html: getHTML(
                'url',
                prettyfyUrl(suggestion.displayUrl || suggestion.url)
              ),
            }}
          />

          <span
            className={styles.instantTermLabel}
            dangerouslySetInnerHTML={{
              __html: getHTML('term', suggestion.term),
            }}
          />

          {type === 'instantResultAd' && (
            <span className={styles.adLabel}>
              {getTranslation('search_suggestion_ad')}
            </span>
          )}
        </span>
      );
    }

    if (
      !['organic', 'organicProvided', 'historyOrganic', 'searchTag'].includes(
        type
      )
    ) {
      return (
        <span className={styles.itemTerm}>
          <span
            dangerouslySetInnerHTML={{
              __html: getHTML('term', suggestion.term),
            }}
          />
          {/* <span className={styles.urlLabel}>
            <span> - </span>
            <span
              dangerouslySetInnerHTML={{ __html: getHTML('url', prettyfyUrl(suggestion.displayUrl || suggestion.url)) }}
            />
          </span> */}
        </span>
      );
    }

    return (
      <span className={styles.itemTerm}>
        <span
          dangerouslySetInnerHTML={{ __html: getHTML('term', suggestion.term) }}
        />
        {type === 'organicProvided' && (
          <span className={styles.openTabLabel}>
            {' '}
            - Search in {ENGINES[engine]?.v || 'OceanHero'}
          </span>
        )}
      </span>
    );
  };

  return (
    <div
      ref={(el) => customRef(suggestion.id, el)}
      id={suggestion.id}
      onMouseEnter={() => (onMouseEnter ? onMouseEnter(suggestion.id) : null)}
      className={cn(
        styles.suggestion,
        styles[type],
        styles[suggestion.inputType],
        {
          [styles.active]: isActive,
        }
      )}
      data-suggestion={type}
    >
      <a href={suggestion.url} onClick={(evt) => onClick(suggestion, evt)}>
        <pre className={styles.menuItem}>
          <div className={cn(styles.iconContainer, isLarge && styles.large)}>
            {renderIcon()}
          </div>

          <div>
            {renderTerm()}

            {suggestion.label &&
              !['instantResult', 'instantResultAd'].includes(type) && (
                <div className={styles.label}>{suggestion.label}</div>
              )}
          </div>
        </pre>
      </a>

      {['history', 'historyOrganic'].includes(type) && (
        <button
          type="button"
          className={styles.removeButton}
          onClick={handleRemove}
        >
          <ClearIcon fontSize="small" />
        </button>
      )}
    </div>
  );
};

export default memo(SuggestionElement);
